// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useAsyncError from 'Hooks/useAsyncError';
import usePermissions from 'Hooks/usePermissions';
import download from 'Utils/download';
import useAnalytics from 'Hooks/useAnalytics';
import InvoicesComponent from 'Components/Reports/Invoices';
import { API_INVOICES } from 'Constants';

const defaultSortId = 'issue_date-desc';

const Invoices = memo(() => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn } = {} } = useUser();
  const { isExternalReseller } = usePermissions();
  const { query, location, push } = useRouter();
  const analytics = useAnalytics();

  const sortOptions = useMemo(() => [
    { id: 'invoice_number-asc', label: `${ t('pages.invoiceNumber')} ${ t('orders.ascending')}` },
    { id: 'invoice_number-desc', label: `${ t('pages.invoiceNumber')} ${ t('orders.descending')}` },
    { id: 'purchase_order-asc', label: `${ t('pages.purchaseOrder')} ${ t('orders.ascending')}` },
    { id: 'purchase_order-desc', label: `${ t('pages.purchaseOrder')} ${ t('orders.descending')}` },
    { id: 'issue_date-asc', label: `${ t('pages.date')} ${ t('orders.ascending')}` },
    { id: 'issue_date-desc', label: `${ t('pages.date')} ${ t('orders.descending')}` },
    { id: 'net_value-asc', label: `${ t('pages.netValue')} ${ t('orders.ascending')}` },
    { id: 'net_value-desc', label: `${ t('pages.netValue')} ${ t('orders.descending')}` },
    { id: 'is_paid-asc', label: `${ t('pages.isPaid')} ${ t('orders.ascending')}` },
    { id: 'is_paid-desc', label: `${ t('pages.isPaid')} ${ t('orders.descending')}` },
  ], []);

  const defaultSort = sortOptions[5];
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  const getInvoices = () => {
    setLoading(true);

    let path = API_INVOICES;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    fetcher(path).then(res => {
      if (res.length === 0) {
        setInvoices([]);
        setPagination(null);
        return;
      }

      const resPagination = {
        currentPage: res?.currentPage,
        recordsPerPage: res?.recordsPerPage,
        totalPages: res?.totalPages,
        totalRecords: res?.totalRecords
      };

      const mappedInvoices = res.invoices.map(invoice => ({
        ...invoice,
        invoiceNumber: {
          number: invoice?.number,
          overdue: invoice?.overdue,
        },
        purchaseOrder: invoice?.purchaseOrder,
        date: invoice?.date,
        netValue: {
          value: invoice?.netValue,
          currency: invoice?.currency,
        },
        totalWithVat: {
          value: invoice?.totalWithVat,
          currency: invoice?.currency,
        },
        isPaid: {
          paid: invoice?.paid,
          paidAmount: invoice?.paidAmount,
          remainingAmount: invoice?.remainingAmount,
          currency: invoice?.currency,
        },
        actions: {
          id: invoice?.id,
        }
      }));

      setInvoices(mappedInvoices);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Invoices on page load
  useEffect(() => {
    if (loggedIn) {
      getInvoices();
    }

    // if (Object.keys(query).length && !loggedIn) {
    //   push({
    //     location: location.pathname,
    //     search: ''
    //   });
    // }
  }, [location.pathname, query, email, loggedIn]);

  const handleDownload = (downloadType, invoiceId) => {
    let path;

    if (downloadType === 'warranty') {
      analytics.track('file_download', { type: 'warranty' });
      path = `${ API_INVOICES }/${invoiceId}/warranty`;
    }

    if (downloadType === 'csv' || downloadType === 'xlsx' || downloadType === 'pdf') {
      analytics.track('file_download', { type: downloadType });
      path = `${ API_INVOICES }/${invoiceId}/export?format=${ downloadType }`;
    }

    download(path);
  };

  const onMobileOrderChange = sort => {
    const [orderBy, orderDirection] = sort.split('-');
    analytics.track('sort_reports', { order_by: orderBy, order_direction: orderDirection });

    if (orderDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: orderBy,
      order_direction: orderDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  return (
    <InvoicesComponent
      invoices={ invoices }
      loading={ loading }
      pagination={ pagination }
      getInvoices={ getInvoices }
      handleMobileOrderChange={ onMobileOrderChange }
      currentSort={ currentSort }
      defaultSortId={ defaultSortId }
      sortOptions={ sortOptions }
      onDownload={ handleDownload }
      isExternalReseller={ isExternalReseller }
    />
  );
});

Invoices.displayName = 'InvoicesContainer';

export default Invoices;
